import { createContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { Context, Dispatch, ReactNode, SetStateAction } from 'react';

import {
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  EMPTY_STRING,
  NULL_FUNCTION,
} from '@e-socna/empty';
import { EMPTY_ID } from '@e-socna/empty-client';
import { OrientationEnum, PaperFormatEnum } from '@e-socna/data-layer';
import type { SectionTypeId } from '@e-socna/data-layer';

import { FormVariant } from '../../../../forms';
import type { MemosState } from '../../../../forms/MemoBeforeSubmit';

import { useEditorActions } from '../../hooks/useEditorActions.hook';
import { useEnrichVirtualRoot } from '../../hooks/useEnrichContent.hook';
import { BackupMode } from '../../enums/backup-mode.enum';
import { EditorMode } from '../../enums/editor-mode.enum';
import { getEditorMode } from '../../utils/get-editor-mode.util';
import type { Editable } from '../../types/editable.type';
import type { DocumentEditorProps } from '../../types/props.type';
import type { DocumentEditorCmp_TemplateDependency } from '../../queries/one-template-dependency.query';
import type { DocumentEditorState } from '../../types/editor-state.type';
import type { DocumentEditorSimpleActions } from '../../types/simple-actions.type';
import type { EnrichedSection } from '../../types/enriched-section.type';

export interface DocumentEditorData<E extends Editable> {
  readonly props: DocumentEditorProps<E>;
  readonly clearSaveMemos: () => void;
  readonly state: DocumentEditorState<E>;
  readonly actions: DocumentEditorSimpleActions<E>;
  readonly handleSaveAction: () => unknown;
  readonly saveMemos: MemosState;
  readonly saveSubmitHandler: () => unknown;
  readonly setSaveMemos: Dispatch<SetStateAction<MemosState>>;
  readonly showMemoBeforeSave: boolean;
  readonly enrichedVirtualRoot: EnrichedSection;
  readonly currentEditorMode: EditorMode;
}

const notImplemented = () => {
  throw new Error('Not implemented');
};

const DocumentEditorContext = createContext<DocumentEditorData<Editable>>({
  props: {
    componentSettings: {
      InfoTabPanel: NULL_FUNCTION,
    },
    formSettings: {
      formVariant: FormVariant.Creation,
      initialEditable: {
        _id: EMPTY_ID,
        createdAt: EMPTY_STRING,
        updatedAt: EMPTY_STRING,
        contentUpdatedAt: EMPTY_STRING,
        creatorId: EMPTY_ID,
        updaterId: EMPTY_ID,
      },
      isLoading: true,
    },
    getters: {
      editable: notImplemented,
      printing: notImplemented,
      storage: notImplemented,
      versions: notImplemented,
    },
    handlers: {
      onSave: notImplemented,
    },
    resolversSettings: {
      allowedReturnTypeIds: EMPTY_ARRAY,
      mustBeResolvableOnBack: false,
      enableDirectGeneration: false,
    },
    routerSettings: {
      editorModePaths: EMPTY_OBJECT,
      isInPrintMode: false,
    },
    saveSettings: {
      askMemo: {
        beforePrint: false,
        beforeSave: false,
      },
      backupMode: BackupMode.Manual,
    },
  },
  clearSaveMemos: NULL_FUNCTION,
  state: {
    pastVersions: EMPTY_ARRAY,
    editable: {
      _id: EMPTY_ID,
      updatedAt: EMPTY_STRING,
      contentUpdatedAt: EMPTY_STRING,
      createdAt: EMPTY_STRING,
      creatorId: EMPTY_ID,
      updaterId: EMPTY_ID,
    },
    futureVersions: EMPTY_ARRAY,
    isDrawerOpen: false,
    currentVersion: 0,
    foldedSectionIds: EMPTY_ARRAY,
  },
  actions: {
    openDrawer: notImplemented,
    closeDrawer: notImplemented,
    foldAllSections: notImplemented,
    unfoldAllSections: notImplemented,
    requestTwoStepContentAction: notImplemented,
    fulfillTwoStepContentAction: notImplemented,
    clearTwoStepContentAction: notImplemented,
    setFoldingMode: notImplemented,
    deleteSection: notImplemented,
    reinitializeSection: notImplemented,
    duplicateSection: notImplemented,
    setPageBreakBefore: notImplemented,
    setCustomPageBreakBefore: notImplemented,
    setPageBreakAfter: notImplemented,
    setCustomPageBreakAfter: notImplemented,
    setOrientation: notImplemented,
    setPaperFormat: notImplemented,
    setDisplayCondition: notImplemented,
    undo: notImplemented,
    redo: notImplemented,
    clearHistory: notImplemented,
    bumpLatestSavedVersion: notImplemented,
    clearNavigationTarget: notImplemented,
    updateInfo: notImplemented,
    updateAdmin: notImplemented,
    move: notImplemented,
  },
  handleSaveAction: notImplemented,
  saveMemos: {
    hasBeenMemoed: false,
    isOpenMemoDialog: false,
  },
  saveSubmitHandler: notImplemented,
  setSaveMemos: notImplemented,
  showMemoBeforeSave: false,
  enrichedVirtualRoot: {
    __typename: 'Section',
    subSections: EMPTY_ARRAY,
    _id: EMPTY_ID,
    typeId: EMPTY_ID as SectionTypeId,
    isFullyLoaded: false,
    realPath: EMPTY_ARRAY,
    alreadyUsedIds: EMPTY_ARRAY,
    contentOriginId: EMPTY_ID,
    hasPagebreakBefore: false,
    hasPagebreakAfter: false,
    orientation: OrientationEnum.Portrait,
    paperFormat: PaperFormatEnum.A4,
    isVirtual: null,
    isInsertionPoint: null,
    title: null,
    content: null,
    template: null,
    customLayouts: null,
    displayCondition: null,
  },
  currentEditorMode: EditorMode.Editor,
});

type Props<E extends Editable> = Readonly<{
  children: ReactNode;
}> &
  DocumentEditorProps<E>;

const DocumentEditorContextProvider = <E extends Editable>({
  children,
  ...props
}: Readonly<Props<E>>) => {
  const [templateDependencies, setTemplateDependencies] =
    useState<readonly DocumentEditorCmp_TemplateDependency[]>(EMPTY_ARRAY);

  const {
    formSettings: { initialEditable },
    saveSettings: { askMemo, backupMode },
    routerSettings: { editorModePaths },
    getters,
    handlers,
  } = props;

  // Stabilize the reference so that ClearHistory always restores to the
  // original value even if the parent re-renders with a new object identity.
  const stableInitialEditable = useMemo(
    () => initialEditable,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialEditable._id],
  );

  const {
    clearSaveMemos,
    state,
    actions,
    handleSaveAction,
    saveMemos,
    saveSubmitHandler,
    setSaveMemos,
    showMemoBeforeSave,
  } = useEditorActions({
    initialEditable: stableInitialEditable,
    templateDependencies,
    backupMode: backupMode ?? BackupMode.Manual,
    askMemoBeforeSave: askMemo?.beforeSave ?? false,
    handleSave: handlers.onSave,
    getters,
  });

  const enrichedVirtualRoot = useEnrichVirtualRoot({
    state,
    templateDependencies,
    setTemplateDependencies,
    getters,
  });

  const { pathname: currentPath } = useLocation();

  const currentEditorMode = getEditorMode(currentPath, editorModePaths);

  const value = useMemo<DocumentEditorData<E>>(
    () => ({
      clearSaveMemos,
      state,
      actions,
      handleSaveAction,
      saveMemos,
      saveSubmitHandler,
      setSaveMemos,
      showMemoBeforeSave,
      enrichedVirtualRoot,
      props: {
        ...props,
        saveSettings: {
          ...props.saveSettings,
          askMemo: {
            ...props.saveSettings.askMemo,
            beforePrint: props.saveSettings.askMemo?.beforePrint ?? false,
            beforeSave: props.saveSettings.askMemo?.beforeSave ?? false,
          },
          backupMode: props.saveSettings.backupMode ?? BackupMode.Manual,
        },
        routerSettings: {
          ...props.routerSettings,
          isInPrintMode: props.routerSettings.isInPrintMode ?? false,
        },
      },
      currentEditorMode,
    }),
    [
      actions,
      clearSaveMemos,
      currentEditorMode,
      enrichedVirtualRoot,
      handleSaveAction,
      props,
      saveMemos,
      saveSubmitHandler,
      setSaveMemos,
      showMemoBeforeSave,
      state,
    ],
  );

  const DEC = DocumentEditorContext as unknown as Context<
    DocumentEditorData<E>
  >;

  return <DEC.Provider value={value}>{children}</DEC.Provider>;
};

export { DocumentEditorContext, DocumentEditorContextProvider };
